import { Component, OnDestroy, OnInit, signal } from '@angular/core';

import { AuthDetailsStore, PaymentsCashierConfig } from '@payments/common-utils';

import { BrandType, Config, InputType, ProductType, QDType } from '../config/appConstants';
import { UrlConstants } from '../config/urlConstants';
import { QDInstrument } from '../model/qdInstrument.model';
import { BehaviorDataService } from '../service/behaviorData.service';
import { BetslipQdApiService } from '../service/betslipqd-api.service';
import { DataService } from '../service/data.service';
import { DepositService } from '../service/deposit.service';
import { GooglePayService } from '../service/google-pay.service';
import { GtmService } from '../service/gtm.service';
import { OptionListService } from '../service/optionList.service';
import { UserDataService } from '../service/userDataService';
import { GPAY_CONSTANTS } from '../shared/error.constants';
import { GVCBetslipComponent } from './betslip/gvc/gvc-betslip.component';
import { LCGBetslipComponent } from './betslip/lcg/lcg-betslip.component';

@Component({
    selector: 'app-qd',
    templateUrl: './qd.component.html',
    imports: [GVCBetslipComponent, LCGBetslipComponent],
})
export class QDComponent implements OnInit, OnDestroy {
    isGvcBetslip = signal<boolean>(true);
    constructor(
        public dataService: DataService,
        public userDataService: UserDataService,
        private behaviorData: BehaviorDataService,
        public optionListService: OptionListService,
        public depositService: DepositService,
        private apiService: BetslipQdApiService,
        private gtmService: GtmService,
        private gpayService: GooglePayService,
        private authDetailsStore: AuthDetailsStore,
        private paymentsCashierConfig: PaymentsCashierConfig,
    ) {
        this.depositService.openCashier();
        this.userDataService.isDataLoading.set(true);
        this.fetchUserData();
    }

    orchestrate() {
        this.optionListService.isDataLoading.set(true);
        this.depositService.isDataLoading.set(true);
        if (this.authDetailsStore.getAuthDetails().authFlag) {
            this.fetchFxRates();
            this.fetchPendingRedeems();
            this.fetchQDData();
        }

        this.loadComponent();
    }
    ngOnInit() {
        this.resetSubmitData();
    }

    createHiddenIframe(url: string): void {
        const iframe = document.createElement('iframe');
        const cacheBuster = `nocache=${Date.now()}`; // Unique timestamp
        iframe.src = url.includes('?') ? `${url}&${cacheBuster}` : `${url}?${cacheBuster}`;
        iframe.style.display = 'none';
        iframe.onload = () => {
            iframe.remove();
        };
        document.body.appendChild(iframe);
    }

    fetchUserData() {
        this.apiService.get(UrlConstants.USER_DATA_URI).subscribe(
            (data) => {
                this.userDataService.userData = data;
                this.behaviorData.setUserConfig(data);
                this.gtmService.loadGTMData();
                if (data?.configParams?.qd_ngpg_iframe_enabled === 'Y') {
                    this.createHiddenIframe(`${this.paymentsCashierConfig.ngpgHost}/cardIframe/cookieUpdate.html`);
                }
                this.userDataService.isDataLoading.set(false);
                if ([BrandType.CORAL, BrandType.LADBROKEUK, BrandType.LADBROKEDE].includes(this.dataService?.sessionInfo?.brandId)) {
                    this.gtmService.raiseEventForDepositCTA();
                    this.gtmService.raiseEventForDepositCTACashierPopupScreenLoad();
                }
                this.gpayService.loadUserInfo(data, this.orchestrate.bind(this));
            },
            (error) => {
                this.dataService.error.set(error);
            },
        );
    }
    loadComponent() {
        this.isGvcBetslip.set(this.depositService?.QDType == 'GVCBETSLIP');
    }

    fetchFxRates() {
        this.apiService.get(UrlConstants.FX_RATES_URI).subscribe(
            (data) => {
                this.depositService.conversionRates = data;
                this.depositService.loadConversionMap();
                this.depositService.isDataLoading.set(false);
            },
            () => {
                this.depositService.isDataLoading.set(false);
            },
        );
    }

    fetchPendingRedeems() {
        this.apiService.get(UrlConstants.REVERSALS_URI).subscribe(
            (data) => {
                if (data?.redeemInfoList?.length > 0) {
                    this.optionListService.reversalExists = true;
                    this.optionListService.reversalArr = data?.redeemInfoList;
                }
            },
            (error) => {
                this.dataService.error.set(error);
            },
        );
    }

    fetchQDData() {
        this.apiService.get(UrlConstants.QD_INSTRUMENTS_URI).subscribe(
            (data) => {
                if (Array.isArray(data) && data.length > 0) {
                    this.optionListService.optionList = data;
                    this.optionListService.selectedOption = data[0];
                    const isBelgiumRgDecreeEnabled = this.userDataService?.userData?.configParams?.belgium_rg_decree_enabled === Config.YES;
                    if (isBelgiumRgDecreeEnabled) {
                        this.getRgLimitData();
                    }
                    this.optionListService.depositAvailable.set(true);

                    this.optionListService.optionList.map((item) => {
                        if (this.isSportsOnlyPayment(item)) {
                            item.sportsOnly = true;
                        } else if (this.isBankAccountsOnlyPayment(item)) {
                            item.bankAccountsOnly = true;
                        }
                    });

                    this.optionListService.optionList = this.filterOptionList();

                    if (this.optionListService?.optionList?.length > 3) {
                        this.optionListService.optionList = this.optionListService?.optionList?.splice(0, 3);
                    }

                    this.handleSingleOptionSelection();
                    if (this.optionListService?.optionList?.length >= 1) {
                        this.gtmLoadEvent();
                        this.depositService.depositInfo.amountInfo.nativeCurrency =
                            this.optionListService?.selectedOption?.paymentMethodInfo?.minDepositLimit?.currencyCode;
                        this.depositService.depositInfo.amountInfo.nativeAmountString = this.depositService.getAmountInAccountCurrency();
                        this.optionListService.isDataLoading.set(false);
                    }
                } else {
                    if (this.depositService.QDType == QDType.GVCBETSLIP) {
                        this.optionListService.depositAvailable.set(false);
                    }
                    this.optionListService.isDataLoading.set(false);
                    this.optionListService.redirectToMainCashier('&type=deposit');
                }
            },
            (error) => {
                this.dataService.error.set(error);
            },
        );
    }

    private gtmLoadEvent() {
        const paymentMethod = this.optionListService?.optionList[0]?.paymentMethodInfo?.paymentMethod;
        const paymentMethodNameMessage = {
            eventName: 'selectedPaymentMethod',
            eventData: {
                paymentMethodName: paymentMethod,
            },
        };
        this.gtmService.raiseEvent('quickDepositLoad', `${paymentMethod?.toLowerCase()} input`, paymentMethod, true);
        this.depositService.postMessageToParent(paymentMethodNameMessage, '*');
    }

    private handleSingleOptionSelection() {
        if (
            this.optionListService.optionList?.length === 0 ||
            (this.optionListService.optionList.length === 1 && this.shouldRedirectToMainCashier())
        ) {
            this.optionListService.redirectToMainCashier();
        }
    }

    private shouldRedirectToMainCashier(): boolean {
        return (
            this.optionListService.optionList[0]?.redirectToInputPage &&
            [InputType.PLAYMGMPREPAIDCARD, InputType.BORGATAPREPAIDCARD, InputType.PARTYPREPAIDCARD, InputType.INSTANTECHECKS].includes(
                this.optionListService.optionList[0]?.paymentMethodInfo?.paymentMethod,
            )
        );
    }

    private isSportsOnlyPayment(item: QDInstrument): boolean {
        return (
            (item.paymentMethodInfo?.paymentMethod === InputType.PAYPAL && item.paymentAccountInfo?.paypalSportsOnlyLogo === Config.YES) ||
            (item.paymentMethodInfo?.paymentMethod === InputType.VISA && item.paymentAccountInfo?.visaSportsOnlyLogo === Config.YES)
        );
    }

    private isBankAccountsOnlyPayment(item: QDInstrument): boolean {
        return item.paymentMethodInfo?.paymentMethod === InputType.PAYPAL && item.paymentAccountInfo?.paypalBankAccountsOnlyLogo === Config.YES;
    }

    filterOptionList(): QDInstrument[] {
        const { paypalOnlyForSports, visaOnlyForSports, enable_prepaidcard_restrictions, rollingweek_remaining_balance } =
            this.userDataService?.userData?.configParams || {};

        const rollingWeekRemainingAmount = Number(rollingweek_remaining_balance);
        const enablePrepaidcardRestrictions = enable_prepaidcard_restrictions === 'Y';

        return this.optionListService.optionList.filter((usedOption) => {
            const isPrepaidLimitReached =
                rollingWeekRemainingAmount === 0 || Number(usedOption?.paymentMethodInfo?.minDepositLimit?.value) > rollingWeekRemainingAmount;
            const paymentMethod = usedOption?.paymentMethodInfo?.paymentMethod || '';

            if (
                this.isPrepaidCardRestricted(usedOption, enablePrepaidcardRestrictions, isPrepaidLimitReached) ||
                this.isGooglePayNotAllowed(paymentMethod) ||
                this.isApplePayOrPix(paymentMethod)
            ) {
                return false;
            }

            return !this.isPaypalOrVisaForSports(paypalOnlyForSports, visaOnlyForSports, paymentMethod);
        });
    }
    private isPrepaidCardRestricted(usedOption: QDInstrument, enablePrepaidcardRestrictions: boolean, isPrepaidLimitReached: boolean): boolean {
        return enablePrepaidcardRestrictions && isPrepaidLimitReached && usedOption.fundingSource?.toLowerCase() === 'prepaid';
    }

    private isGooglePayNotAllowed(paymentMethod: string): boolean {
        return paymentMethod === GPAY_CONSTANTS.GOOGLEPAY && !this.gpayService.showGooglePayButton();
    }

    private isApplePayOrPix(paymentMethod: string): boolean {
        const excludedPaymentMethods = [InputType.APPLEPAYDIRECT, 'PIX'];
        return excludedPaymentMethods.includes(paymentMethod);
    }

    private isPaypalOrVisaForSports(paypalOnlyForSports: string, visaOnlyForSports: string, paymentMethod: string): boolean {
        const { walletBucketType } = this.dataService?.sessionInfo || {};

        if (paypalOnlyForSports === Config.YES || visaOnlyForSports === Config.YES) {
            if (walletBucketType === InputType.PAYPAL) {
                return this.isPaypalForSports(paypalOnlyForSports, paymentMethod);
            } else if (walletBucketType === InputType.NONPAYPAL) {
                return this.isNonPaypalWalletForSports(paypalOnlyForSports, visaOnlyForSports, paymentMethod);
            } else if (paymentMethod === InputType.APPLEPAY) {
                return true;
            } else {
                return this.isSportsBookOrBettingProduct();
            }
        }
        return false;
    }

    private isPaypalForSports(paypalOnlyForSports: string, paymentMethod: string): boolean {
        return paypalOnlyForSports === Config.YES && paymentMethod === InputType.PAYPAL;
    }

    private isNonPaypalWalletForSports(paypalOnlyForSports: string, visaOnlyForSports: string, paymentMethod: string): boolean {
        if (paypalOnlyForSports === Config.YES && paymentMethod === InputType.PAYPAL) {
            return true;
        } else if (visaOnlyForSports === Config.YES && paymentMethod === InputType.VISA) {
            return true;
        }
        return false;
    }

    private isSportsBookOrBettingProduct(): boolean {
        return [ProductType.SPORTSBOOK, ProductType.BETTING].includes(this.dataService?.sessionInfo?.productId);
    }

    getRgLimitData() {
        this.apiService.get(UrlConstants.GET_RG_LIMIT_INFO).subscribe((response) => {
            if (response.length > 0) {
                const inputData: any = {};
                inputData.rglimitInfo = response;
                const rgbucketlimits = this.userDataService?.userData?.configParams?.rg_bucket_threshold_limits.split(',');
                if (rgbucketlimits?.length > 0) {
                    inputData.rg_first_bucket_threshold = rgbucketlimits[0];
                    inputData.rg_second_bucket_threshold = rgbucketlimits[1];
                    inputData.rg_third_bucket_threshold = rgbucketlimits[2];
                }
                this.dataService.RgLimitData = inputData;
            }
        });
    }
    resetSubmitData() {
        this.depositService.submitResponse = null;
        this.depositService.showDepositFailure.set(false);
        this.depositService.showDepositSuccess.set(false);
    }

    ngOnDestroy() {
        this.resetSubmitData();
    }
}
